import { useEffect, useMemo, useState } from "react";
import { Col, Form, InputNumber, Modal, Row } from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { FaDollarSign } from "react-icons/fa";
import { FaFileInvoiceDollar } from "react-icons/fa6";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import ConfirmModal from "../../../global/components/ConfirmModal";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PageNotFound } from "../../../global/components/PageNotFound";
import MyPaymentForm from "../../../global/components/PaymentForm";
import useDelete from "../../../global/hooks/useDelete";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import { hasRole } from "../../../global/hooks/getUserRole";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import PaymentIcon from "../../../assets/images/vectors/SideBarIcons/PaymentIcon";
import {
  getOptionLabel,
  renderPrice,
  termOptions,
} from "../../../utils/Common";
import { PurchaseLicenseCodeSelector } from "../../MySite/Components/PurchaseLicenseCodeSelector";
import {
  getPurchaseListPath,
  getPurchaseOpenPath,
} from "../utils/listSearchStorage";

const PRE_PURCHASE_TYPE = 1;

const hasValue = (value) => value != null && value !== "";

const formatMoney = (value) =>
  hasValue(value) && Number.isFinite(Number(value))
    ? `$${Number(value).toFixed(2)}`
    : "";

const getPendingKey = (codeNo) => `pending-${codeNo}`;

const getItemsColumns = ({ onCountChange }) => [
  {
    title: "Item",
    dataIndex: "ItemName",
    key: "ItemName",
    onHeaderCell: () => ({ onClick: () => {} }),
  },
  {
    title: "Code",
    dataIndex: "CodeName",
    key: "CodeName",
    onHeaderCell: () => ({ onClick: () => {} }),
  },
  {
    title: "Term",
    dataIndex: "Term",
    key: "Term",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: (value) => getOptionLabel(termOptions, Number(value)) || "",
  },
  {
    title: "Unit Price",
    dataIndex: "Price",
    key: "Price",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: renderPrice,
  },
  {
    title: "Count",
    dataIndex: "ItemCount",
    key: "ItemCount",
    width: 130,
    align: "center",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: (value, record) =>
      record.isPending && onCountChange ? (
        <InputNumber
          size="small"
          min={1}
          max={9999}
          precision={0}
          value={value}
          onChange={(next) => onCountChange(record.key, next)}
          style={{ width: 90 }}
        />
      ) : (
        (value ?? 1)
      ),
  },
  {
    title: "Price",
    key: "Amount",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: (_, record) =>
      renderPrice(Number(record.Price || 0) * Number(record.ItemCount || 1)),
  },
];

export const PrePurchaseForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const canManagePurchase = hasRole(1, 2);
  const isSystemUser = hasRole(1, 2, 3);

  const [pendingItems, setPendingItems] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [showItemsDeleteModal, setShowItemsDeleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [showCardLayout, setShowCardLayout] = useState(!isSystemUser);
  const [savingItems, setSavingItems] = useState(false);
  const [paidPayment, setPaidPayment] = useState(null);

  const purchaseUrl = id
    ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`
    : null;
  const itemsUrl = id
    ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/`
    : null;
  const {
    data: purchaseData,
    loading: purchaseLoading,
    error: purchaseError,
    refetch: purchaseRefetch,
  } = useGet(purchaseUrl);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);

  const { postData: postItem } = usePost(itemsUrl);
  const { error: putError, putData: putPurchase } = usePut(purchaseUrl);
  const {
    data: deletedPurchase,
    error: deletedPurchaseError,
    deleteData: deletePurchase,
  } = useDelete(purchaseUrl);
  const {
    data: deletedItems,
    error: deletedItemsError,
    deleteMultiple: deleteItems,
  } = useDelete(
    id
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/bulk-delete/`
      : null,
  );

  const purchase = purchaseData?.data;

  const { data: optionData } = useGet(
    `${process.env.REACT_APP_API_URL}/api/accounts/options/`,
  );
  const resolvedOemNo = useMemo(() => {
    const value = purchase?.OemNo;
    if (!hasValue(value)) return null;
    if (Number.isFinite(Number(value))) return Number(value);
    return (
      optionData?.data?.oems?.find((oem) => String(oem?.name) === String(value))
        ?.value ?? null
    );
  }, [purchase?.OemNo, optionData]);
  const { data: dealerOptions, loading: dealerOptionsLoading } = useGet(
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null,
  );
  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptions, null),
    [dealerOptions],
  );

  const savedItems = useMemo(
    () =>
      (itemsData?.data ?? [])
        .map((item) => ({ ...item, key: item.ItemNo, isPending: false }))
        .sort((a, b) => b.ItemNo - a.ItemNo),
    [itemsData],
  );
  const tableRows = [...pendingItems, ...savedItems];
  const pendingAmount = pendingItems.reduce(
    (sum, row) => sum + Number(row.Price || 0) * Number(row.ItemCount || 1),
    0,
  );
  const savedSubTotal = Number(purchase?.SubTotal ?? purchase?.Amount) || 0;
  const savedTotal = Number(purchase?.Total ?? purchase?.Amount) || 0;
  const paymentAmount = savedTotal + pendingAmount;

  useEffect(() => {
    if (!purchase) return;
    form.setFieldsValue({
      OemName: hasValue(purchase.OemNo) ? String(purchase.OemNo) : "",
      DealerNo: hasValue(purchase.DealerNo) ? Number(purchase.DealerNo) : null,
      SubTotal: formatMoney(savedSubTotal + pendingAmount),
      Discount: formatMoney(purchase.Discount ?? 0),
      Total: formatMoney(paymentAmount),
    });
  }, [form, purchase, savedSubTotal, pendingAmount, paymentAmount]);

  useEffect(() => {
    if (!purchase || paymentModal || savingItems || paidPayment) return;
    const openPath = getPurchaseOpenPath(purchase, canManagePurchase);
    if (openPath !== `/purchase/prepurchase/${id}`) {
      navigate(openPath, { replace: true });
    }
  }, [
    purchase,
    canManagePurchase,
    id,
    navigate,
    paymentModal,
    savingItems,
    paidPayment,
  ]);

  useEffect(() => {
    if (!paymentModal) setShowCardLayout(!isSystemUser);
  }, [paymentModal, isSystemUser]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [putError]);

  useEffect(() => {
    if (deletedPurchaseError) {
      openErrorNotification(
        deletedPurchaseError?.response?.data?.message || "Something went wrong",
      );
    } else if (deletedPurchase) {
      openConfirmNotification("Success");
      navigate(getPurchaseListPath());
    }
  }, [deletedPurchase, deletedPurchaseError, navigate]);

  useEffect(() => {
    if (deletedItemsError) {
      openErrorNotification(
        deletedItemsError?.response?.data?.message || "Something went wrong",
      );
    } else if (deletedItems) {
      openConfirmNotification("Success");
      itemsRefetch();
      purchaseRefetch();
    }
  }, [deletedItems, deletedItemsError, itemsRefetch, purchaseRefetch]);

  if (!id || purchaseError || (!purchaseLoading && purchaseData && !purchase)) {
    return <PageNotFound />;
  }

  const onPickItem = (code) => {
    setPendingItems((prev) => [
      {
        key: getPendingKey(code.CodeNo),
        isPending: true,
        CodeNo: code.CodeNo,
        Item: code.Item,
        ItemName: code.ItemName,
        CodeName: code.CodeName,
        Term: code.Term,
        Price: code.Price,
        ItemCount: 1,
      },
      ...prev,
    ]);
  };

  const onCountChange = (key, count) =>
    setPendingItems((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, ItemCount: count || 1 } : row,
      ),
    );

  const onDeleteItemsConfirm = async () => {
    const savedKeys = selectedRowKeys.filter(
      (key) => !String(key).startsWith("pending-"),
    );
    setPendingItems((prev) =>
      prev.filter((row) => !selectedRowKeys.includes(row.key)),
    );
    setSelectedRowKeys([]);
    if (savedKeys.length) await deleteItems({ ids: savedKeys });
  };

  const savePendingItems = async () => {
    const failed = [];
    for (const row of pendingItems) {
      const result = await postItem({
        CodeNo: Number(row.CodeNo),
        ItemCount: Number(row.ItemCount) || 1,
      });
      if (result == null) failed.push(row);
    }
    setPendingItems(failed);
    return failed.length === 0;
  };

  const getPaymentNo = (paymentResponse) => {
    const payment =
      paymentResponse?.data?.data ?? paymentResponse?.data ?? paymentResponse;
    return payment?.PaymentNo ?? payment?.payment_no ?? payment?.id ?? null;
  };

  const getPurchasePaidPayload = (paymentResponse, amount) => ({
    PurchaseTime: purchase?.PurchaseTime
      ? dayjs(purchase.PurchaseTime).format("YYYY-MM-DD HH:mm:ss")
      : null,
    PurchaseType: purchase?.PurchaseType ?? PRE_PURCHASE_TYPE,
    OemNo: resolvedOemNo ?? purchase?.OemNo,
    DealerNo: purchase?.DealerNo,
    Amount: amount,
    PaymentNo: getPaymentNo(paymentResponse),
  });

  // Items are only written once the payment has gone through; a failed save keeps the
  // payment response so the retry never charges again.
  const finalizePaidPurchase = async (paymentResponse, amount) => {
    setSavingItems(true);
    const saved = pendingItems.length ? await savePendingItems() : true;
    const putResult = saved
      ? await putPurchase(getPurchasePaidPayload(paymentResponse, amount))
      : null;
    if (!saved || putResult == null) {
      setSavingItems(false);
      setPaidPayment({ response: paymentResponse, amount });
      itemsRefetch();
      openErrorNotification(
        saved
          ? "Payment received, but the purchase could not be updated. Click Save Items to retry."
          : "Payment received, but some items could not be saved. Click Save Items to retry.",
      );
      return;
    }
    // Info reads the same SWR cache entry; without this it sees the stale unpaid
    // purchase and bounces straight back here.
    await purchaseRefetch(
      (current) => ({
        ...current,
        data: {
          ...current?.data,
          ...putResult?.data,
          PaymentNo:
            putResult?.data?.PaymentNo ??
            getPaymentNo(paymentResponse) ??
            current?.data?.PaymentNo,
          PaymentTime:
            putResult?.data?.PaymentTime ??
            current?.data?.PaymentTime ??
            dayjs().format("YYYY-MM-DD HH:mm:ss"),
        },
      }),
      { revalidate: true },
    );
    setSavingItems(false);
    setPaidPayment(null);
    navigate(`/purchase/info/${id}`, { replace: true });
  };

  const onPayment = () => {
    if (paidPayment) {
      finalizePaidPurchase(paidPayment.response, paidPayment.amount);
      return;
    }
    if (!(paymentAmount > 0)) {
      openErrorNotification("Total must be greater than 0 to make a payment.");
      return;
    }
    setPaymentModal(true);
  };

  const leavePage = () => navigate(getPurchaseListPath());

  const breadcrumbItems = [
    { label: "Purchase", href: "/purchase" },
    { label: "Pre-Purchase", href: `/purchase/prepurchase/${id}` },
  ];

  const buttonsData = [
    {
      label: paidPayment ? "Save Items" : "Payment",
      icon: paidPayment ? <CheckCircleOutlined /> : <FaDollarSign />,
      onClick: onPayment,
      loading: savingItems,
      disabled: savingItems || !tableRows.length || purchaseLoading,
    },
    ...(paidPayment
      ? []
      : [
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => setShowDeleteModal(true),
            disabled: savingItems,
          },
        ]),
    {
      label: "List",
      icon: <UnorderedListOutlined />,
      onClick: () =>
        pendingItems.length ? setShowLeaveModal(true) : leavePage(),
    },
  ];

  const itemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: () => setItemModalOpen(true),
      disabled: savingItems,
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowItemsDeleteModal(true),
      disabled: savingItems || !selectedRowKeys.length,
    },
  ];

  return (
    <>
      <Form form={form} layout="vertical" autoComplete="off">
        <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
          <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
            <CommonButton buttonsData={buttonsData} />
          </CommonBreadcrumb>
        </div>

        <div className="bg-white overflow-hidden border rounded-[4px]">
          <div className="flex items-center bg-gray-200 p-2.5">
            <FaFileInvoiceDollar className="text-[18px] text-black" />
            <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
              Pre-Purchase
            </span>
          </div>
          <div className="px-2 pt-1 pb-3">
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="OEM"
                  name="OemName"
                  disabled
                  loading={purchaseLoading}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomSelect
                  label="Dealer"
                  name="DealerNo"
                  options={modifiedDealers}
                  disabled
                  placeholder="Please select"
                  loading={purchaseLoading || dealerOptionsLoading}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="SubTotal"
                  name="SubTotal"
                  disabled
                  loading={purchaseLoading}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="Discount"
                  name="Discount"
                  disabled
                  loading={purchaseLoading}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="Total"
                  name="Total"
                  disabled
                  loading={purchaseLoading}
                />
              </Col>
            </Row>
          </div>
        </div>
      </Form>

      {!paidPayment && (
        <div className="flex justify-end mb-2">
          <CommonButton buttonsData={itemButtonsData} />
        </div>
      )}
      <GlobalTable
        columns={getItemsColumns({
          onCountChange: paidPayment ? null : onCountChange,
        })}
        dataSource={tableRows}
        loading={itemsLoading}
        rowSelection={
          paidPayment
            ? undefined
            : { selectedRowKeys, onChange: setSelectedRowKeys }
        }
      />

      <Modal
        open={itemModalOpen}
        closable={false}
        onCancel={() => setItemModalOpen(false)}
        footer={null}
        width={1000}
        centered
        destroyOnClose
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaFileInvoiceDollar className="text-[18px] text-black" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add Pre-Purchase Item
            </span>
          </div>
        }
      >
        <PurchaseLicenseCodeSelector
          purchaseId={id}
          allowDuplicateItems
          disabledCodeNos={pendingItems.map((row) => row.CodeNo)}
          onPick={onPickItem}
          onCancel={() => setItemModalOpen(false)}
        />
      </Modal>

      <ConfirmModal
        showModal={showItemsDeleteModal}
        setShowModal={setShowItemsDeleteModal}
        onDeleteConfirm={onDeleteItemsConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <ConfirmModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        onDeleteConfirm={deletePurchase}
      />
      <ConfirmModal
        showModal={showLeaveModal}
        setShowModal={setShowLeaveModal}
        onDeleteConfirm={leavePage}
        content="Unsaved items will be discarded. Do you want to leave?"
      />

      <Modal
        title={
          <div className="flex items-center bg-gray-200 p-2.5 rounded-md">
            <PaymentIcon className="text-[18px] text-black" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Make Payment
            </span>
          </div>
        }
        open={paymentModal}
        onCancel={() => setPaymentModal(false)}
        footer={null}
        closeIcon={false}
        width={560}
        centered
        destroyOnClose
      >
        <MyPaymentForm
          DealerNo={purchase?.DealerNo}
          amount={formatMoney(paymentAmount) || "$0.00"}
          setPaymentModal={setPaymentModal}
          InvoiceNo={id}
          OrderName={
            purchase?.PurchaseTime ? String(purchase.PurchaseTime) : "Purchase"
          }
          refetch={purchaseRefetch}
          showCardLayout={showCardLayout}
          setShowCardLayout={setShowCardLayout}
          onPaymentSuccess={async (paymentResponse) => {
            setPaymentModal(false);
            await finalizePaidPurchase(paymentResponse, paymentAmount);
          }}
        />
      </Modal>
    </>
  );
};
